import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MDRBadge } from "@/components/MDRBadge";
import { ManagerDeviceAlertsBell } from "@/components/manager/ManagerDeviceAlerts";
import {
  LayoutDashboard, CalendarRange, Megaphone, Settings, BarChart3, Boxes,
  Menu, ArrowLeftRight, LogOut, ClipboardList
} from "lucide-react";

const PHARMACY_ID = "ph1";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

const navItems = [
  { path: "/manager", label: "Panoramica", icon: LayoutDashboard, end: true },
  { path: "/manager/planning", label: "Pianificazione", icon: CalendarRange },
  { path: "/manager/bookings", label: "Prenotazioni", icon: ClipboardList },
  { path: "/manager/campaigns", label: "Campagne", icon: Megaphone },
  { path: "/manager/monitoring", label: "Monitoraggio", icon: BarChart3 },
  { path: "/manager/inventory", label: "Scorte", icon: Boxes },
  { path: "/manager/configuration", label: "Configurazione", icon: Settings }
];

export const ManagerLayout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, logout } = useAppContext();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
          isActive ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <item.icon className="h-5 w-5" />
      {item.label}
    </NavLink>
  );

  const Sidebar = ({ onItemClick }) => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b px-4">
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </div>
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 border-2 border-indigo-200">
            <AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold">
              {currentUser?.name?.charAt(0) || "R"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-sm leading-tight truncate">{currentUser?.name || "Responsabile"}</p>
            <p className="text-xs text-muted-foreground">Responsabile Farmacia</p>
          </div>
        </div>
        <MDRBadge variant="admin" size="sm" className="mt-3" />
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map(item => <NavItem key={item.path} item={item} onClick={onItemClick} />)}
        </nav>
      </ScrollArea>
      <div className="border-t p-3 space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={() => navigate("/")}
          data-testid="manager-switch-profile-btn"
        >
          <ArrowLeftRight className="h-4 w-4" />
          Cambia profilo
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          onClick={() => { logout(); navigate("/login"); }}
        >
          <LogOut className="h-4 w-4" />
          Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <Sidebar />
      </aside>
      {/* Desktop top bar with bell */}
      <header className="hidden md:flex sticky top-0 z-40 h-14 items-center justify-end gap-2 border-b bg-card/80 backdrop-blur px-6 md:pl-[17rem]">
        <ManagerDeviceAlertsBell pharmacyId={PHARMACY_ID} dataTestId="manager-bell-desktop" />
      </header>
      <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-card px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <Sidebar onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
        <div className="ml-auto">
          <ManagerDeviceAlertsBell pharmacyId={PHARMACY_ID} dataTestId="manager-bell-mobile" />
        </div>
      </header>
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-3.5rem)]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default ManagerLayout;
